import { useCallback, useEffect, useState } from "react";
import { useBackendReconnectKey } from "@/hooks/use-backend-connectivity";

import {
  isWorkspaceScopedStartSupported,
  startWorkflow,
  WorkflowApiError,
} from "@/api/workflows";
import type { AccessTokenGetter } from "@/lib/utils";
import i18n from "@/lib/i18n";

export type WorkflowTriggerState =
  | { phase: "idle" }
  | { phase: "confirming" }
  | { phase: "starting" }
  | { phase: "queued"; taskId: string }
  | { phase: "error"; message: string };

export interface UseWorkflowTriggerOptions {
  fileName: string;
  agentId?: string | null;
  inputs: Record<string, unknown>;
  getToken: AccessTokenGetter;
  /**
   * Optional caller-owned confirmation. It can use a dialog, native confirm,
   * or another approval UI and resolves to whether the start should continue.
   */
  confirm?: () => boolean | Promise<boolean>;
  onQueued?: (taskId: string, fileName: string) => void;
  sessionId?: string;
}

export interface UseWorkflowTriggerResult {
  /** null while capability discovery is pending; false means do not start. */
  isWorkspaceScopedStartSupported: boolean | null;
  state: WorkflowTriggerState;
  trigger: () => Promise<void>;
  triggerFor: (fileName: string, confirm?: () => boolean | Promise<boolean>, actionId?: string) => Promise<void>;
}

function triggerErrorMessage(error: unknown): string {
  if (error instanceof WorkflowApiError) {
    return error.message || i18n.t("workflowTrigger.startFailed");
  }
  return error instanceof Error ? error.message : i18n.t("workflowTrigger.startFailed");
}

/**
 * Capability-gated state machine for a form or other explicit workflow start
 * control. The capability probe runs once for each mounted hook and never
 * retries unsupported servers automatically.
 */
export function useWorkflowTrigger({
  fileName,
  agentId,
  inputs,
  getToken,
  confirm,
  onQueued,
  sessionId,
}: UseWorkflowTriggerOptions): UseWorkflowTriggerResult {
  const [isSupported, setIsSupported] = useState<boolean | null>(null);
  const [state, setState] = useState<WorkflowTriggerState>({ phase: "idle" });
  const backendReconnectKey = useBackendReconnectKey();

  useEffect(() => {
    let cancelled = false;
    setIsSupported(null);
    void isWorkspaceScopedStartSupported(getToken)
      .then((supported) => {
        if (!cancelled) setIsSupported(supported);
      });
    return () => {
      cancelled = true;
    };
  }, [backendReconnectKey, getToken]);

  const triggerFor = useCallback(async (targetFileName: string, approval?: () => boolean | Promise<boolean>, actionId?: string) => {
    if (
      isSupported !== true
      || state.phase === "confirming"
      || state.phase === "starting"
    ) {
      return;
    }

    if (approval) {
      setState({ phase: "confirming" });
      try {
        if (!await approval()) {
          setState({ phase: "idle" });
          return;
        }
      } catch (error: unknown) {
        setState({ phase: "error", message: triggerErrorMessage(error) });
        return;
      }
    }

    setState({ phase: "starting" });
    try {
      const result = await startWorkflow(targetFileName, { agentId, inputs, sessionId, composerAction: actionId }, getToken);
      setState({ phase: "queued", taskId: result.task_id });
      onQueued?.(result.task_id, targetFileName);
    } catch (error: unknown) {
      setState({ phase: "error", message: triggerErrorMessage(error) });
    }
  }, [agentId, getToken, inputs, isSupported, onQueued, sessionId, state.phase]);

  const trigger = useCallback(() => triggerFor(fileName, confirm), [triggerFor, fileName, confirm]);

  return {
    isWorkspaceScopedStartSupported: isSupported,
    state,
    trigger,
    triggerFor,
  };
}
